import React from "react";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

const BulletList = ({ items, editing, onChange }) => {
  const { t } = useTranslation();
  if (!editing) {
    if (!items?.length) return null;
    return (
      <Box component="ul" sx={{ m: 0, pl: 2.25, "& li": { mb: 0.35 } }}>
        {items.map((item, idx) => (
          <Typography key={`${item}-${idx}`} component="li" variant="body2">
            {item}
          </Typography>
        ))}
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      {(items || []).map((item, idx) => (
        <Stack key={idx} direction="row" spacing={0.5} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={1}
            value={item}
            onChange={(e) => {
              const next = [...items];
              next[idx] = e.target.value;
              onChange(next);
            }}
          />
          <IconButton
            size="small"
            color="error"
            onClick={() => onChange(items.filter((_, i) => i !== idx))}
            aria-label={t("organigrama.roleCard.delete")}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <IconButton
        size="small"
        onClick={() => onChange([...(items || []), ""])}
        sx={{ alignSelf: "flex-start" }}
        aria-label={t("organigrama.roleCard.add")}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

const RoleTable = ({ table, accent }) => {
  if (!table?.rows?.length) return null;
  return (
    <TableContainer
      sx={{
        mt: 1,
        mb: 0.5,
        border: "1px solid",
        borderColor: alpha(accent, 0.2),
        borderRadius: 1,
      }}
    >
      {table.caption ? (
        <Typography variant="caption" sx={{ display: "block", px: 1, pt: 1, fontWeight: 700 }}>
          {table.caption}
        </Typography>
      ) : null}
      <Table size="small">
        {table.headers?.length ? (
          <TableHead>
            <TableRow>
              {table.headers.map((header) => (
                <TableCell key={header} sx={{ fontWeight: 700, py: 0.75 }}>
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {table.rows.map((row, idx) => (
            <TableRow key={idx}>
              {(Array.isArray(row) ? row : [row.area, row.decision, row.cofo]).map((cell, cellIdx) => (
                <TableCell key={cellIdx} sx={{ py: 0.65, fontSize: "0.8125rem" }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ResponsibilityBlock = ({ section, editing, onChange, accent }) => {
  const { t } = useTranslation();
  const updateSection = (patch) => onChange({ ...section, ...patch });

  const updateGroup = (groupIdx, patch) => {
    const groups = [...(section.groups || [])];
    groups[groupIdx] = { ...groups[groupIdx], ...patch };
    updateSection({ groups });
  };

  return (
    <Box sx={{ mb: 2 }}>
      {editing ? (
        <TextField
          size="small"
          fullWidth
          label={t("organigrama.roleCard.responsibilityArea")}
          value={section.title || ""}
          onChange={(e) => updateSection({ title: e.target.value })}
          sx={{ mb: 1 }}
        />
      ) : (
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
          {section.title}
        </Typography>
      )}

      {section.intro ? (
        <Typography variant="body2" sx={{ mb: 0.75, whiteSpace: "pre-wrap" }}>
          {section.intro}
        </Typography>
      ) : null}

      <BulletList
        items={section.items}
        editing={editing}
        onChange={(items) => updateSection({ items })}
      />

      {(section.groups || []).map((group, groupIdx) => (
        <Box key={groupIdx} sx={{ mt: 1, pl: editing ? 0 : 1 }}>
          {editing ? (
            <TextField
              size="small"
              fullWidth
              label={t("organigrama.roleCard.groupSubtitleOptional")}
              value={group.label || ""}
              onChange={(e) => updateGroup(groupIdx, { label: e.target.value || null })}
              sx={{ mb: 0.75 }}
            />
          ) : group.label ? (
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {group.label}
            </Typography>
          ) : null}
          <BulletList
            items={group.items}
            editing={editing}
            onChange={(items) => updateGroup(groupIdx, { items })}
          />
        </Box>
      ))}

      {section.extraItems?.length || editing ? (
        <Box sx={{ mt: 1 }}>
          <BulletList
            items={section.extraItems || []}
            editing={editing}
            onChange={(extraItems) => updateSection({ extraItems })}
          />
        </Box>
      ) : null}

      {!editing && section.table ? <RoleTable table={section.table} accent={accent} /> : null}

      {editing ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <IconButton
            size="small"
            onClick={() =>
              updateSection({
                groups: [...(section.groups || []), { label: "", items: [""] }],
              })
            }
            aria-label={t("organigrama.roleCard.addGroup")}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}
    </Box>
  );
};

const RoleCard = ({ role, editing, onChange }) => {
  const { t } = useTranslation();
  const accent = role.accent || "#1976d2";

  const updateRole = (patch) => onChange({ ...role, ...patch });

  const updateResponsabilidad = (idx, section) => {
    const responsabilidades = [...role.responsabilidades];
    responsabilidades[idx] = section;
    updateRole({ responsabilidades });
  };

  const hasFoco = (role.foco?.length || 0) > 0 || (role.noFoco?.length || 0) > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(accent, 0.35),
        height: "100%",
        backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.06)} 0%, transparent 120px)`,
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: accent, fontWeight: 800, letterSpacing: 0.5 }}
      >
        {role.section} {role.name}
      </Typography>

      {editing ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <TextField
            size="small"
            label={t("organigrama.roleCard.subtitleOptional")}
            fullWidth
            value={role.subtitle || ""}
            onChange={(e) => updateRole({ subtitle: e.target.value || null })}
          />
          <TextField
            size="small"
            label={t("organigrama.roleCard.role")}
            fullWidth
            value={role.role || ""}
            onChange={(e) => updateRole({ role: e.target.value })}
          />
          <TextField
            size="small"
            label={t("organigrama.roleCard.backupFunctionalOptional")}
            fullWidth
            value={role.backupFuncional || ""}
            onChange={(e) => updateRole({ backupFuncional: e.target.value || null })}
          />
          <TextField
            size="small"
            label={t("organigrama.roleCard.reportsToOptional")}
            fullWidth
            value={role.reportaA || ""}
            onChange={(e) => updateRole({ reportaA: e.target.value || null })}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("organigrama.roleCard.reportsToListOptional")}
          </Typography>
          <BulletList
            items={role.reportaAItems || []}
            editing
            onChange={(reportaAItems) =>
              updateRole({ reportaAItems: reportaAItems.length ? reportaAItems : null })
            }
          />
          <TextField
            size="small"
            label={t("organigrama.roleCard.collaboratesWithOptional")}
            fullWidth
            multiline
            minRows={2}
            value={role.colaboraCon || ""}
            onChange={(e) => updateRole({ colaboraCon: e.target.value || null })}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {t("organigrama.roleCard.collaboratesWithListOptional")}
          </Typography>
          <BulletList
            items={role.colaboraConItems || []}
            editing
            onChange={(colaboraConItems) =>
              updateRole({ colaboraConItems: colaboraConItems.length ? colaboraConItems : null })
            }
          />
        </Stack>
      ) : (
        <>
          {role.subtitle ? (
            <Typography
              variant="caption"
              sx={{ display: "block", fontWeight: 800, letterSpacing: 0.6, color: accent, mb: 0.5 }}
            >
              {role.subtitle}
            </Typography>
          ) : null}
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25, mb: 0.25 }}>
            {role.role}
          </Typography>
          {role.reportaAItems?.length ? (
            <Box sx={{ mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 0.35 }}>
                {t("organigrama.roleCard.reportsTo")}:
              </Typography>
              <BulletList items={role.reportaAItems} editing={false} />
            </Box>
          ) : role.reportaA ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {role.reportaALabel || t("organigrama.roleCard.reportsTo")}:{" "}
              </Box>
              {role.reportaA}
            </Typography>
          ) : null}
          {role.backupFuncional ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {t("organigrama.roleCard.backupFunctional")}:{" "}
              </Box>
              {role.backupFuncional}
            </Typography>
          ) : null}
          {role.colaboraConItems?.length ? (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mb: 0.35 }}>
                {t("organigrama.roleCard.collaboratesWith")}:
              </Typography>
              <BulletList items={role.colaboraConItems} editing={false} />
            </Box>
          ) : role.colaboraCon ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {t("organigrama.roleCard.collaboratesWith")}:{" "}
              </Box>
              {role.colaboraCon}
            </Typography>
          ) : null}
        </>
      )}

      {hasFoco ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {t("organigrama.roleCard.foco")}
              </Typography>
              {editing ? (
                <BulletList
                  items={role.foco}
                  editing
                  onChange={(foco) => updateRole({ foco })}
                />
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {role.foco.map((item) => (
                    <Chip key={item} label={item} size="small" color="success" variant="outlined" />
                  ))}
                </Stack>
              )}
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {t("organigrama.roleCard.noFoco")}
              </Typography>
              {editing ? (
                <BulletList
                  items={role.noFoco}
                  editing
                  onChange={(noFoco) => updateRole({ noFoco })}
                />
              ) : (
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {role.noFoco.map((item) => (
                    <Chip key={item} label={item} size="small" variant="outlined" />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </>
      ) : null}

      {role.proposito ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
            {role.propositoLabel || t("organigrama.roleCard.purposeOfRole")}
          </Typography>
          {editing ? (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={role.proposito || ""}
              onChange={(e) => updateRole({ proposito: e.target.value })}
              sx={{ mt: 0.75 }}
            />
          ) : (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
              {role.proposito}
            </Typography>
          )}
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
        {role.misionLabel || t("organigrama.roleCard.mision")}
      </Typography>
      {editing ? (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={role.mision || ""}
          onChange={(e) => updateRole({ mision: e.target.value })}
          sx={{ mt: 0.75 }}
        />
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
          {role.mision}
        </Typography>
      )}

      {role.scopeItems?.length ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
            {role.scopeLabel || t("organigrama.roleCard.scopeOfResponsibility")}
          </Typography>
          {role.scopeIntro ? (
            <Typography variant="body2" sx={{ mt: 0.5, mb: 0.5 }}>
              {role.scopeIntro}
            </Typography>
          ) : null}
          <BulletList items={role.scopeItems} editing={editing} onChange={(scopeItems) => updateRole({ scopeItems })} />
          {role.scopeNote ? (
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
              {role.scopeNote}
            </Typography>
          ) : null}
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
        {role.responsabilidadesLabel || t("organigrama.roleCard.responsabilidades")}
      </Typography>
      <Box sx={{ mt: 1 }}>
        {(role.responsabilidades || []).map((section, idx) => (
          <ResponsibilityBlock
            key={`${section.title}-${idx}`}
            section={section}
            editing={editing}
            onChange={(next) => updateResponsabilidad(idx, next)}
            accent={accent}
          />
        ))}
      </Box>

      {role.governanceBoundaries?.length ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
            {role.governanceBoundariesLabel || t("organigrama.roleCard.governanceBoundaries")}
          </Typography>
          {role.governanceBoundariesIntro ? (
            <Typography variant="body2" sx={{ mt: 0.5, mb: 0.5 }}>
              {role.governanceBoundariesIntro}
            </Typography>
          ) : null}
          <BulletList
            items={role.governanceBoundaries}
            editing={editing}
            onChange={(governanceBoundaries) => updateRole({ governanceBoundaries })}
          />
        </>
      ) : null}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
        {role.kpisLabel || t("organigrama.roleCard.kpis")}
      </Typography>
      <Box sx={{ mt: 0.75 }}>
        {(role.kpiGroups || []).length > 0 ? (
          (role.kpiGroups || []).map((group, idx) => (
            <Box key={`${group.title}-${idx}`} sx={{ mb: 1.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
                {group.title}
              </Typography>
              <BulletList
                items={group.items}
                editing={editing}
                onChange={(items) => {
                  const kpiGroups = [...role.kpiGroups];
                  kpiGroups[idx] = { ...kpiGroups[idx], items };
                  updateRole({ kpiGroups });
                }}
              />
            </Box>
          ))
        ) : (
          <BulletList
            items={role.kpis}
            editing={editing}
            onChange={(kpis) => updateRole({ kpis })}
          />
        )}
      </Box>

      {role.leadershipExpectations ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
            {role.leadershipExpectationsLabel || t("organigrama.roleCard.leadershipExpectations")}
          </Typography>
          {editing ? (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={4}
              value={role.leadershipExpectations || ""}
              onChange={(e) => updateRole({ leadershipExpectations: e.target.value })}
              sx={{ mt: 0.75 }}
            />
          ) : (
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
              {role.leadershipExpectations}
            </Typography>
          )}
        </>
      ) : null}

      {role.decisionMatrix ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent }}>
            {role.decisionMatrixLabel || t("organigrama.roleCard.executiveDecisionMatrix")}
          </Typography>
          <RoleTable table={role.decisionMatrix} accent={accent} />
        </>
      ) : null}
    </Paper>
  );
};

export default RoleCard;
